<script setup lang="ts">
import { stripLocalePrefix } from '~/utils/i18n/locale';
import { isLearnPath } from '~/constants/portalNav';
type TabKey = 'dashboard' | 'deals' | 'projects' | 'learn' | 'profile';

type Tab = {
  key: TabKey;
  label: string;
  to: string;
};

const { t } = useI18n();
const route = useRoute();

// iOS Safari pushes `position: fixed; bottom: 0` elements off-screen when
// the on-screen keyboard opens and (during URL-bar transitions) can briefly
// detach them from the visual viewport. Track the visualViewport and hide
// the bar while a soft keyboard is up, then bring it back the moment the
// viewport returns to its layout size.
const keyboardOpen = ref(false);
const KEYBOARD_THRESHOLD_PX = 100;

let cleanup: (() => void) | null = null;
onMounted(() => {
  if (typeof window === 'undefined' || !window.visualViewport) return;
  const vv = window.visualViewport;
  const update = () => {
    keyboardOpen.value = window.innerHeight - vv.height > KEYBOARD_THRESHOLD_PX;
  };
  update();
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  cleanup = () => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
  };
});
onUnmounted(() => cleanup?.());

// 5 slots in the bottom bar: 4 tabs + 1 FAB. Masterclass leads get full
// agent-equivalent access, so they see the same Deals tab as everyone else —
// only the Masterclass course content stays gated behind the $197 unlock.
// Learn covers the Masterclass, Market & Sales and the Developer Vault.
const tabs = computed<Tab[]>(() => {
  return [
    { key: 'dashboard', label: t('nav.items.dashboard'), to: '/dashboard' },
    { key: 'deals', label: t('nav.items.dealsShort'), to: '/deals' },
    { key: 'projects', label: t('nav.items.projects'), to: '/explore' },
    { key: 'learn', label: t('nav.items.learn'), to: '/learn' },
  ];
});

const paths = computed(() => ({
  dashboard: '/dashboard',
  deals: '/deals',
  projects: '/explore',
  profile: '/profile',
}));

const activeKey = computed<TabKey | null>(() => {
  const p = stripLocalePrefix(route.path);

  if (p.startsWith(paths.value.profile)) return 'profile';
  if (p.startsWith(paths.value.deals)) return 'deals';
  if (p.startsWith(paths.value.projects)) return 'projects';
  if (isLearnPath(p)) return 'learn';
  if (p.startsWith(paths.value.dashboard)) return 'dashboard';

  return null;
});

const fabHref = computed(() => '/deals/register');
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <nav
        v-show="!keyboardOpen"
        class="bottom-bar fixed inset-x-0 bottom-0 z-50 grid h-[82px] items-start border-t border-black/[0.08] desktop:hidden"
        style="grid-template-columns: 1fr 1fr 78px 1fr 1fr"
        :aria-label="t('nav.aria.primary')"
      >
        <NuxtLink
          :to="tabs[0].to"
          class="tab-btn"
          :class="{ 'is-active': activeKey === tabs[0].key }"
          :aria-current="activeKey === tabs[0].key ? 'page' : undefined"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z" />
          </svg>
          <span>{{ tabs[0].label }}</span>
        </NuxtLink>

        <NuxtLink
          :to="tabs[1].to"
          class="tab-btn"
          :class="{ 'is-active': activeKey === tabs[1].key }"
          :aria-current="activeKey === tabs[1].key ? 'page' : undefined"
        >
          <!-- Deals (briefcase) icon -->
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="6"
              width="18"
              height="13"
              rx="2"
            />
            <path d="M16 13h3M3 10h18" />
          </svg>
          <span>{{ tabs[1].label }}</span>
        </NuxtLink>

        <NuxtLink
          :to="fabHref"
          class="fab-btn"
          :aria-label="t('nav.bottomTabBar.registerDealAria')"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </NuxtLink>

        <NuxtLink
          :to="tabs[2].to"
          class="tab-btn"
          :class="{ 'is-active': activeKey === tabs[2].key }"
          :aria-current="activeKey === tabs[2].key ? 'page' : undefined"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
          <span>{{ tabs[2].label }}</span>
        </NuxtLink>

        <NuxtLink
          :to="tabs[3].to"
          class="tab-btn"
          :class="{ 'is-active': activeKey === tabs[3].key }"
          :aria-current="activeKey === tabs[3].key ? 'page' : undefined"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          <span>{{ tabs[3].label }}</span>
        </NuxtLink>
      </nav>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
.bottom-bar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 10px 10px calc(env(safe-area-inset-bottom, 0px));
}

.tab-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 6px 4px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6e6f73; // black-60
  transition: color 0.15s ease;

  svg {
    width: 22px;
    height: 22px;
    color: #6e6f73;
    transition: color 0.15s ease;
  }

  &.is-active {
    color: #ffb648; // gold-primary-100
  }

  &.is-active svg {
    color: #ffb648;
  }
}

.fab-btn {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #0d0f15; // black-100
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  margin-top: -18px;
  box-shadow:
    0 10px 24px rgba(13, 15, 21, 0.28),
    0 0 0 4px rgba(255, 182, 72, 0.18);
  ring-offset-shadow: none;
  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease;

  svg {
    width: 22px;
    height: 22px;
    color: #ffb648;
  }

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
